import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { environment } from 'src/environments/environment';
import { ProgramariUltimaOraService } from '../programari-ultima-ora.service';
import { NotificationService } from 'src/app/services/notification.service';
import { AuthService } from 'src/app/services/auth.service';
import { finalize } from 'rxjs';
import { helper } from 'src/environments/helper';
import { SlotGrup, SlotZi, SlotGrupUpdate } from '../programari-ultima-ora.interface';

@Component({
    selector: 'app-edit-programari-ultima-ora',
    templateUrl: './edit-programari-ultima-ora.component.html',
    styleUrls: ['./edit-programari-ultima-ora.component.scss']
})
export class EditProgramariUltimaOraComponent implements OnInit {
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    helper = helper;

    token!: string;
    isLoading = false;

    // Date grup
    grupInfo: SlotGrup;
    zile: SlotZi[] = [];
    emails: string = '';

    // Pentru adăugare zi nouă
    showAddZi = false;
    newZi: SlotZi = { day_of_week: -1, time_from: '' };

    zileSaptamana: { key: string; value: number }[] = [
        { key: 'Luni', value: 1 },
        { key: 'Marți', value: 2 },
        { key: 'Miercuri', value: 3 },
        { key: 'Joi', value: 4 },
        { key: 'Vineri', value: 5 },
        { key: 'Sâmbătă', value: 6 },
        { key: 'Duminică', value: 0 },
    ];

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: SlotGrup & { isGroupEdit?: boolean },
        private dialogRef: MatDialogRef<EditProgramariUltimaOraComponent>,
        private programariSloturiUltimaOraService: ProgramariUltimaOraService,
        private notificationService: NotificationService,
        private authService: AuthService
    ) {
        this.grupInfo = data;
        // Clonăm zilele pentru a evita modificarea directă a datelor originale
        this.zile = data.zile ? data.zile.map(z => ({
            id: z.id,
            day_of_week: z.day_of_week,
            time_from: this.formatTimeForInput(z.time_from)
        })) : [];
        this.emails = data.emails || '';
        this.processToken();
    }

    ngOnInit(): void {
    }

    formatTimeForInput(time: string): string {
        if (!time) return '';
        // Extrage doar HH:MM
        const match = time.match(/(\d{2}):(\d{2})/);
        if (match) {
            return `${match[1]}:${match[2]}`;
        }
        return time;
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    processToken() {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token) => {
            this.token = token;
            this.programariSloturiUltimaOraService.setToken(token);
        });
    }

    getNumeZi(dayOfWeek: number): string {
        const zi = this.zileSaptamana.find(z => z.value === dayOfWeek);
        return zi ? zi.key : 'N/A';
    }

    get zileSaptamanaDisponibile(): { key: string; value: number }[] {
        const zileExistente = this.zile.map(z => z.day_of_week);
        return this.zileSaptamana.filter(z => !zileExistente.includes(z.value));
    }

    removeZi(index: number) {
        if (this.zile.length <= 1) {
            this.notificationService.warningSwal(
                this.errorTitle,
                'Trebuie să existe cel puțin o zi configurată.',
                this.errorIcon
            );
            return;
        }
        this.zile.splice(index, 1);
    }

    addZi() {
        if (this.newZi.day_of_week === -1) {
            this.notificationService.warningSwal(
                this.errorTitle,
                'Selectați o zi din săptămână.',
                this.errorIcon
            );
            return;
        }
        if (!this.newZi.time_from) {
            this.notificationService.warningSwal(
                this.errorTitle,
                'Selectați ora.',
                this.errorIcon
            );
            return;
        }

        // Verifică dacă ziua există deja
        if (this.zile.some(z => z.day_of_week === this.newZi.day_of_week)) {
            this.notificationService.warningSwal(
                this.errorTitle,
                'Această zi este deja configurată.',
                this.errorIcon
            );
            return;
        }

        this.zile.push({
            day_of_week: this.newZi.day_of_week,
            time_from: this.newZi.time_from
        });

        // Sortează zilele după day_of_week
        this.zile.sort((a, b) => {
            // Luni=1, ..., Duminică=0 -> transformăm 0 în 7 pentru sortare corectă
            const aVal = a.day_of_week === 0 ? 7 : a.day_of_week;
            const bVal = b.day_of_week === 0 ? 7 : b.day_of_week;
            return aVal - bVal;
        });

        // Reset form
        this.newZi = { day_of_week: -1, time_from: '' };
        this.showAddZi = false;
    }

    cancelAddZi() {
        this.newZi = { day_of_week: -1, time_from: '' };
        this.showAddZi = false;
    }

    saveGroup() {
        // Validări
        if (this.zile.length === 0) {
            this.notificationService.warningSwal(
                this.errorTitle,
                'Trebuie să existe cel puțin o zi configurată.',
                this.errorIcon
            );
            return;
        }

        // Verifică că toate zilele au ore
        for (const zi of this.zile) {
            if (!zi.time_from) {
                this.notificationService.warningSwal(
                    this.errorTitle,
                    `Selectați ora pentru ${this.getNumeZi(zi.day_of_week)}.`,
                    this.errorIcon
                );
                return;
            }
        }

        this.isLoading = true;

        const updateData: SlotGrupUpdate = {
            id_modul: this.grupInfo.id_modul,
            id_locatie: this.grupInfo.id_locatie,
            id_serviciu: this.grupInfo.id_serviciu,
            emails: this.emails || '',
            zile: this.zile.map(z => ({
                day_of_week: z.day_of_week,
                time_from: z.time_from
            }))
        };

        this.programariSloturiUltimaOraService
            .updateGroup(updateData)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: () => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }
}
